import React, { useEffect } from 'react';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

interface AdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  headerAction?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

/**
 * Reusable Admin Modal Component
 * Consistent modal structure across admin section
 * 
 * @param isOpen - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @param title - Modal header title
 * @param children - Modal body content
 * @param size - Modal width (default: 'xl')
 * @param showCloseButton - Show X button in header (default: true)
 * @param headerAction - Optional control rendered on the right of the header,
 *                       before the close button (e.g. an "Add New" button)
 * @param footer - Optional footer content
 * @param className - Additional CSS classes for modal content
 */
const AdminModal: React.FC<AdminModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'xl',
  showCloseButton = true,
  headerAction,
  footer,
  className = '',
}) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.scrollTo(0, 0);
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll while the modal is open. Deliberately NOT setting
      // touch-action: none here — iOS Safari has a long-standing WebKit bug
      // where touch-action: none on an ancestor (body) can veto any
      // descendant (like the backdrop below) trying to re-enable panning,
      // leaving the whole page unable to receive touch-scroll gestures at
      // all until reload. overflow: hidden alone is the same lock already
      // used elsewhere in the app (see .app-shell-locked in index.css) and
      // doesn't have that failure mode.
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Remove the inline override so body falls back to the stylesheet
      // (overflow: hidden). Setting "unset" resolves to "visible", which lets
      // Stripe's fixed iframes below the viewport create extra scroll space.
      document.body.style.overflow = '';
      window.scrollTo(0, 0);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Get size classes
  const getSizeClass = (): string => {
    switch (size) {
      case 'sm':
        return 'max-w-sm';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-lg';
      case 'xl':
        return 'max-w-xl';
      case '2xl':
        return 'max-w-2xl';
      case '3xl':
        return 'max-w-3xl';
      default:
        return 'max-w-xl';
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent event propagation on modal content
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center w-full max-w-full overflow-hidden bg-black/50 p-3 sm:p-6 overscroll-contain"
      style={{
        paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 20px) + 0.5rem)',
      }}
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-title"
    >
      <div
        className={`relative w-full mx-auto flex flex-col min-h-0 bg-white rounded-2xl shadow-2xl overflow-hidden ${getSizeClass()} ${className}`}
        style={{
          maxHeight: 'min(84vh, calc(100svh - 3rem))',
          height: 'auto',
        }}
        onClick={handleModalClick}
      >
        {/* Modal Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 md:p-5 border-b border-gray-200 bg-white z-10">
          <h3
            id="modal-title"
            className="text-lg font-semibold text-grease-100 truncate"
          >
            {title}
          </h3>
          <div className="flex items-center gap-3 shrink-0">
            {headerAction}
            {showCloseButton && (
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center transition"
                onClick={onClose}
                aria-label="Close modal"
              >
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto p-4 md:p-5">
          {children}
        </div>

        {/* Modal Footer (Optional) */}
        {footer && (
          <div className="flex-shrink-0 flex items-center justify-end gap-3 p-4 md:p-5 border-t border-gray-200 bg-white z-10">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminModal;

